import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Camera, Download, Image as ImageIcon, Loader2, Sparkles,
  Trash2, UploadCloud, Wand2,
} from "lucide-react";
import { getBackendUrl } from "@/lib/backend";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/apiError";
import { downsizeForUpload } from "@/lib/imageDownsize";

const BACKEND = getBackendUrl();
const API = `${BACKEND}/api`;

// Build the auth-gated image URL for a PhotoRoom job result. The
// `/api/user-images/<uid>/<gen_id>.png` endpoint accepts session cookies
// (sent automatically by <img>) so we don't need a signed token here.
// We append `?v=<completed_at>` so the browser refreshes when a job
// transitions from queued → done at the same gen_id slot.
const photoroomImageUrl = (userId, imageId, completedAt) => {
  if (!userId || !imageId) return "";
  const bust = completedAt ? `?v=${encodeURIComponent(completedAt)}` : "";
  return `${BACKEND}/api/user-images/${userId}/${imageId}.png${bust}`;
};

const QUALITY_TIERS = [
  { id: "hd",            label: "HD",            edge: 1024, cost: 1, hint: "Fast · web/social" },
  { id: "ultra_hd",      label: "Ultra HD",      edge: 2048, cost: 2, hint: "Catalogues · e-commerce" },
  { id: "4k",            label: "KeyShot 4K",    edge: 4096, cost: 3, hint: "Hi-res KeyShot studio render · Gemini 3 Pro" },
  // Print Master 8K (fal.ai AuraSR v2 super-res) is built + tested but
  // temporarily HIDDEN from the dropdown while we finalise pricing and
  // marketing. The backend tier + credit cost remain wired so we can
  // re-enable instantly by flipping `hidden: false`. Do NOT remove —
  // every code path downstream (semaphore, ETA baselines, progress
  // pipeline, fal_upscaler) already knows about it.
  { id: "print_master",  label: "Print Master 8K", edge: 7680, cost: 5, hint: "Banners · billboards · 300 DPI print", badge: "NEW", hidden: true },
];

// Tiers visible in the UI. We keep the full list in QUALITY_TIERS for
// lookup (`tier` selector by id) but only `VISIBLE_QUALITY_TIERS` is
// rendered in the dropdown / hint logic.
const VISIBLE_QUALITY_TIERS = QUALITY_TIERS.filter((q) => !q.hidden);

const FORMAT_OPTIONS = [
  { id: "jpeg", label: "JPEG · white background", credits: 1 },
  { id: "png",  label: "PNG · transparent background", credits: 1 },
  { id: "both", label: "Both formats", credits: 2 },
];

export default function PhotoRoomPage() {
  const { user, loading: authLoading } = useAuth();
  const [quality, setQuality] = useState("hd");
  const [outputFormat, setOutputFormat] = useState("jpeg");
  const [files, setFiles] = useState([]); // [{ id, file, previewUrl }]
  const [jobs, setJobs] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [credits, setCredits] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const inputRef = useRef(null);

  // ----- Auth guard -----
  useEffect(() => {
    if (!authLoading && !user) window.location.replace("/signin");
  }, [authLoading, user]);

  // ----- Load existing TouchUp gallery -----
  useEffect(() => {
    if (!user) return;
    refreshJobs();
    refreshCredits();
  }, [user]);  // eslint-disable-line react-hooks/exhaustive-deps

  const refreshJobs = async () => {
    try {
      const r = await axios.get(`${API}/photoroom/jobs?limit=200`);
      setJobs(r.data?.jobs || []);
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't load TouchUp gallery."));
    }
  };

  const refreshCredits = async () => {
    try {
      const r = await axios.get(`${API}/studio/quota`);
      setCredits(r.data);
    } catch (_e) { /* non-fatal */ }
  };

  // ----- Poll for live job updates while anything is queued/processing -----
  useEffect(() => {
    if (!user) return;
    const active = jobs.some((j) => j.status === "queued" || j.status === "processing");
    if (!active) return;
    const timer = setInterval(() => { refreshJobs(); refreshCredits(); }, 4000);
    return () => clearInterval(timer);
  }, [jobs, user]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ----- File handling -----
  const addFiles = (incoming) => {
    const next = [...files];
    for (const f of incoming) {
      if (!f.type?.startsWith("image/")) continue;
      if (next.length >= 25) {
        toast.error("Maximum 25 images per batch.");
        break;
      }
      next.push({ id: crypto.randomUUID(), file: f, previewUrl: URL.createObjectURL(f) });
    }
    setFiles(next);
  };

  const removeFile = (id) => {
    setFiles((curr) => {
      const target = curr.find((x) => x.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return curr.filter((x) => x.id !== id);
    });
  };

  const clearFiles = () => {
    files.forEach((f) => URL.revokeObjectURL(f.previewUrl));
    setFiles([]);
  };

  // ----- Cost math (frontend) -----
  const tier = useMemo(() => QUALITY_TIERS.find((q) => q.id === quality), [quality]);
  const formatCount = outputFormat === "both" ? 2 : 1;
  const perImage = tier ? tier.cost * formatCount : 0;
  const totalCost = perImage * files.length;
  const canAfford = credits == null ? true : (credits.remaining ?? 0) >= totalCost;

  // ----- Submit batch -----
  // Uploads files ONE AT A TIME (sequentially) to /photoroom/enqueue-one.
  // Each file is FIRST downsized client-side to ≤4096 px longest edge
  // (Gemini's effective input ceiling) so even on a 1-2 Mbps Indian
  // uplink each upload completes within Cloudflare's 100 s window. The
  // backend then runs Gemini + (optionally) AuraSR in parallel — gated
  // by IMAGE_GEN_SEMAPHORE=4 / PHOTOROOM_PRINT_MASTER_SEMAPHORE=1.
  //
  // We auto-retry each upload ONCE on Cloudflare 524 / origin 5xx /
  // network errors. Retrying twice on top of the shrink almost always
  // recovers — failures we surface to the user are real (auth, quota,
  // invalid file).
  const [uploadProgress, setUploadProgress] = useState(null); // { done, total }

  const uploadOne = async (file) => {
    // Shrink huge DSLR/phone images before they ever hit the wire.
    let prepared;
    try { prepared = await downsizeForUpload(file); }
    catch { prepared = file; }   // worst case: send original

    const fd = new FormData();
    fd.append("quality", quality);
    fd.append("output_format", outputFormat);
    fd.append("file", prepared);

    const attempt = () => axios.post(`${API}/photoroom/enqueue-one`, fd, {
      timeout: 90 * 1000,  // upload-only — Gemini runs in background.
      headers: { "Content-Type": "multipart/form-data" },
    });

    try {
      return await attempt();
    } catch (e) {
      const status = e?.response?.status;
      const retriable = !status || status === 524 || status === 502 || status === 503 || status === 504;
      if (!retriable) throw e;
      // One automatic retry — covers Cloudflare edge blips + transient
      // origin overload. Anything that fails twice is almost certainly
      // a real client-side problem.
      await new Promise((r) => setTimeout(r, 1500));
      return attempt();
    }
  };

  const submitBatch = async () => {
    if (!files.length) return;
    if (!canAfford) {
      toast.error(`Not enough credits — need ${totalCost}, you have ${credits?.remaining}.`);
      return;
    }
    setProcessing(true);
    setUploadProgress({ done: 0, total: files.length });
    let okCount = 0;
    let failCount = 0;
    let firstError = null;
    for (let i = 0; i < files.length; i++) {
      const { file } = files[i];
      try {
        const r = await uploadOne(file);
        if (r.data?.quota) setCredits(r.data.quota);
        okCount += 1;
      } catch (e) {
        failCount += 1;
        if (!firstError) firstError = formatApiError(e, "Upload failed.");
        // If the user ran out of credits mid-batch, stop the loop —
        // no point in trying the rest, they'll all hit the same 402.
        if (e?.response?.status === 402) break;
      }
      setUploadProgress({ done: i + 1, total: files.length });
      // Refresh the gallery after each successful upload so the user
      // sees jobs appear live as they queue up.
      if (okCount > 0 && okCount % 3 === 0) refreshJobs();
    }
    setUploadProgress(null);
    if (okCount > 0) {
      toast.success(`Queued ${okCount} image${okCount === 1 ? "" : "s"} for touch-up.`);
    }
    if (failCount > 0) {
      toast.error(`${failCount} upload${failCount === 1 ? "" : "s"} failed — ${firstError}`);
    }
    clearFiles();
    refreshJobs();
    refreshCredits();
    setProcessing(false);
  };

  // ----- Selection helpers (for "Download selected") -----
  const doneJobs = useMemo(() => jobs.filter((j) => j.status === "done"), [jobs]);
  const allSelectableIds = useMemo(() => doneJobs.map((j) => j.id), [doneJobs]);
  const selectedCount = selectedIds.size;
  const allSelected = allSelectableIds.length > 0 &&
    allSelectableIds.every((id) => selectedIds.has(id));

  const toggleSelect = (jobId) => {
    setSelectedIds((curr) => {
      const next = new Set(curr);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };
  const selectAll = () => setSelectedIds(new Set(allSelectableIds));
  const clearSelection = () => setSelectedIds(new Set());

  // Prune deleted/un-done jobs out of the selection automatically.
  useEffect(() => {
    setSelectedIds((curr) => {
      const valid = new Set(allSelectableIds);
      const filtered = new Set();
      for (const id of curr) if (valid.has(id)) filtered.add(id);
      return filtered.size === curr.size ? curr : filtered;
    });
  }, [allSelectableIds]);

  // ----- Bulk download (all or selected) -----
  const downloadIds = async (ids, label) => {
    if (!ids.length) {
      toast.error("Nothing ready to download yet.");
      return;
    }
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/photoroom/download-batch`, {
        params: { ids: ids.join(",") },
        responseType: "blob",
        // Print Master 8K ZIPs can legitimately take 5-10 min to download
        // on a slow Indian uplink. Default axios timeout (0 = no timeout)
        // is what we want — but we set it explicitly to a generous 20 min
        // so a stuck origin still eventually fails the request instead of
        // hanging the user's tab indefinitely.
        timeout: 20 * 60 * 1000,
      });
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `facetra-photoroom-${Date.now()}.zip`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${ids.length} ${label}.`);
    } catch (e) {
      toast.error(formatApiError(e, "Download failed."));
    } finally {
      setDownloading(false);
    }
  };

  const downloadAll = () =>
    downloadIds(allSelectableIds, allSelectableIds.length === 1 ? "touch-up" : "touch-ups");
  const downloadSelected = () =>
    downloadIds(Array.from(selectedIds), selectedCount === 1 ? "touch-up" : "touch-ups");

  const deleteJob = async (jobId) => {
    try {
      await axios.delete(`${API}/photoroom/jobs/${jobId}`);
      setJobs((curr) => curr.filter((j) => j.id !== jobId));
      toast.success("Deleted.");
    } catch (e) {
      toast.error(formatApiError(e, "Delete failed."));
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] text-white grid place-items-center">
        <Loader2 className="w-6 h-6 animate-spin opacity-60" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white" data-testid="photoroom-page">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-[#0B0B0B]/95 backdrop-blur border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <a
              href="/app"
              data-testid="photoroom-back-link"
              className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-[#D4AF37] transition"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Studio
            </a>
            <span className="text-white/20">/</span>
            <h1 className="text-sm font-medium tracking-wide flex items-center gap-2">
              <Camera className="w-4 h-4 text-[#D4AF37]" /> PhotoRoom · TouchUp
            </h1>
          </div>
          {credits && (
            <div className="text-xs text-white/60" data-testid="photoroom-credit-counter">
              <span className="text-[#D4AF37] font-semibold">{credits.remaining}</span> credits remaining
            </div>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid gap-8 lg:grid-cols-[1fr,360px]">
        {/* Upload + settings */}
        <section>
          <div className="mb-6">
            <h2 className="text-lg font-medium">Drop raw photographs → get catalogue-ready touch-ups</h2>
            <p className="text-sm text-white/55 mt-1">
              Cleans dust + scratches, sharpens metalwork, boosts gemstone fire & luster. Preserves every existing element — no AI restructuring.
            </p>
          </div>

          {/* Drop zone */}
          <div
            data-testid="photoroom-drop-zone"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              addFiles(Array.from(e.dataTransfer.files));
            }}
            onClick={() => inputRef.current?.click()}
            className="rounded-2xl border-2 border-dashed border-white/15 hover:border-[#D4AF37]/40 transition p-10 grid place-items-center text-center cursor-pointer bg-[#141414]"
          >
            <UploadCloud className="w-10 h-10 text-[#D4AF37]/70 mb-3" />
            <div className="text-sm font-medium">Drop up to 25 images, or click to browse</div>
            <div className="text-xs text-white/45 mt-1">JPEG · PNG · WebP · HEIC · up to 25 MB each</div>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              data-testid="photoroom-file-input"
              onChange={(e) => {
                addFiles(Array.from(e.target.files || []));
                e.target.value = "";
              }}
            />
          </div>

          {/* Selected files */}
          {files.length > 0 && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-white/55">{files.length} selected</div>
                <button
                  onClick={clearFiles}
                  className="text-xs text-white/55 hover:text-red-300"
                  data-testid="photoroom-clear-files-btn"
                >Clear all</button>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3" data-testid="photoroom-selected-grid">
                {files.map((f) => (
                  <div key={f.id} className="relative group rounded-lg overflow-hidden bg-[#141414] aspect-square">
                    <img src={f.previewUrl} alt={f.file.name} className="w-full h-full object-cover" />
                    <button
                      onClick={() => removeFile(f.id)}
                      className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/70 opacity-0 group-hover:opacity-100 transition text-red-300"
                      title="Remove"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TouchUp gallery */}
          <div className="mt-12">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <h3 className="text-sm font-medium flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#D4AF37]" /> TouchUp gallery
              </h3>
              {doneJobs.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={allSelected ? clearSelection : selectAll}
                    data-testid="photoroom-toggle-select-all-btn"
                    className="text-xs px-3 py-1.5 rounded-full border border-white/15 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition"
                  >
                    {allSelected ? "Clear selection" : `Select all (${doneJobs.length})`}
                  </button>
                  {selectedCount > 0 && (
                    <button
                      onClick={downloadSelected}
                      disabled={downloading}
                      data-testid="photoroom-download-selected-btn"
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#E6C24A] transition disabled:opacity-40"
                    >
                      {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                      Download selected ({selectedCount})
                    </button>
                  )}
                  <button
                    onClick={downloadAll}
                    disabled={downloading}
                    data-testid="photoroom-download-all-btn"
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-white/15 hover:border-[#D4AF37]/40 hover:text-[#D4AF37] transition disabled:opacity-40"
                  >
                    {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                    Download all as ZIP
                  </button>
                </div>
              )}
            </div>

            {jobs.length === 0 ? (
              <div className="rounded-xl border border-white/5 bg-[#141414] p-8 grid place-items-center text-center text-sm text-white/50">
                <ImageIcon className="w-6 h-6 opacity-50 mb-2" />
                Your touch-ups will appear here.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="photoroom-gallery-grid">
                {jobs.map((j) => (
                  <JobCard
                    key={j.id}
                    job={j}
                    userId={user.user_id}
                    selected={selectedIds.has(j.id)}
                    onToggleSelect={() => toggleSelect(j.id)}
                    onDelete={() => deleteJob(j.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Settings sidebar */}
        <aside className="lg:sticky lg:top-20 h-fit space-y-5">
          <div className="rounded-2xl bg-[#141414] border border-white/5 p-5">
            <h3 className="text-sm font-medium mb-4 flex items-center gap-2">
              <Wand2 className="w-4 h-4 text-[#D4AF37]" /> Output settings
            </h3>

            <label className="block text-xs text-white/55 mb-1.5">Quality</label>
            <select
              value={quality}
              onChange={(e) => setQuality(e.target.value)}
              data-testid="photoroom-quality-select"
              className="w-full rounded-lg bg-[#0B0B0B] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-[#D4AF37]/50"
            >
              {VISIBLE_QUALITY_TIERS.map((q) => (
                <option key={q.id} value={q.id}>
                  {q.badge ? `${q.badge} · ` : ""}{q.label} · longest edge {q.edge}px · {q.cost} cr/format
                </option>
              ))}
            </select>
            {tier?.hint && (
              <div className="mt-1.5 text-[11px] text-white/45" data-testid="photoroom-quality-hint">
                {tier.hint}
                {quality === "print_master" && (
                  <span className="block text-[#D4AF37]/80 mt-1">
                    ✨ Diffusion super-resolution via fal.ai · takes ~40-90s/image
                  </span>
                )}
              </div>
            )}

            <label className="block text-xs text-white/55 mb-1.5 mt-4">Format</label>
            <select
              value={outputFormat}
              onChange={(e) => setOutputFormat(e.target.value)}
              data-testid="photoroom-format-select"
              className="w-full rounded-lg bg-[#0B0B0B] border border-white/10 px-3 py-2 text-sm focus:outline-none focus:border-[#D4AF37]/50"
            >
              {FORMAT_OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>

            <div className="mt-5 pt-4 border-t border-white/5 text-xs text-white/65 space-y-1">
              <div className="flex justify-between"><span>Per image</span><span className="text-white">{perImage} cr</span></div>
              <div className="flex justify-between"><span>Images</span><span className="text-white">{files.length}</span></div>
              <div className="flex justify-between font-medium text-sm pt-1">
                <span className="text-white/80">Total cost</span>
                <span className={canAfford ? "text-[#D4AF37]" : "text-red-300"} data-testid="photoroom-total-cost">{totalCost} credits</span>
              </div>
            </div>

            <button
              onClick={submitBatch}
              disabled={!files.length || processing || !canAfford}
              data-testid="photoroom-submit-btn"
              className="mt-5 w-full inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full bg-[#D4AF37] text-black text-sm font-medium hover:bg-[#E6C24A] transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {processing ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> {uploadProgress ? `Uploading ${uploadProgress.done}/${uploadProgress.total}…` : "Processing…"}</>
              ) : (
                <><Sparkles className="w-4 h-4" /> Touch up {files.length || ""} image{files.length === 1 ? "" : "s"}</>
              )}
            </button>
            {!canAfford && (
              <div className="mt-3 text-xs text-red-300">
                Need {totalCost - (credits?.remaining ?? 0)} more credit{totalCost - (credits?.remaining ?? 0) === 1 ? "" : "s"}.
                <a href="/app" className="ml-1 underline">Top up</a>.
              </div>
            )}
          </div>

          <div className="rounded-2xl bg-[#141414] border border-white/5 p-5 text-xs text-white/55">
            <div className="font-medium text-white/75 mb-2">What we DON&apos;T do</div>
            <ul className="space-y-1.5 list-disc pl-5">
              <li>Add new gemstones, motifs, or chain links</li>
              <li>Add initials, logos, hallmarks, or engraved text</li>
              <li>Recolour the metal or change stone shapes</li>
              <li>Restructure the piece or change composition</li>
            </ul>
            <div className="font-medium text-white/75 mt-4 mb-2">What we DO</div>
            <ul className="space-y-1.5 list-disc pl-5">
              <li>Clean dust, scratches, sensor noise</li>
              <li>Sharpen existing metalwork detail</li>
              <li>Boost gemstone fire & sparkle</li>
              <li>Polish metal to mirror finish</li>
              <li>Upscale to your chosen resolution</li>
            </ul>
          </div>
        </aside>
      </main>
    </div>
  );
}

function JobCard({ job, userId, selected, onToggleSelect, onDelete }) {
  const isDone = job.status === "done";
  const isFailed = job.status === "failed";
  const previewId = job.jpeg_image_id || job.png_image_id;
  const previewUrl = previewId
    ? photoroomImageUrl(userId, previewId, job.completed_at)
    : null;

  return (
    <div
      className={`rounded-xl overflow-hidden bg-[#141414] border group transition ${
        selected ? "border-[#D4AF37] ring-2 ring-[#D4AF37]/30" : "border-white/5"
      }`}
      data-testid={`photoroom-job-${job.id}`}
    >
      <div className="aspect-square bg-[#0B0B0B] relative">
        {previewUrl ? (
          <img src={previewUrl} alt={job.source_filename} className="w-full h-full object-contain" />
        ) : (
          <div className="w-full h-full grid place-items-center text-xs text-white/40 px-3">
            {isFailed ? (
              <span className="text-red-300 text-center">{job.error || "Failed"}</span>
            ) : (
              <JobProgress job={job} />
            )}
          </div>
        )}

        {/* Selection checkbox — only meaningful once the touch-up is ready */}
        {isDone && onToggleSelect && (
          <label
            className={`absolute top-1.5 left-1.5 inline-flex items-center justify-center w-6 h-6 rounded-md cursor-pointer transition select-none ${
              selected
                ? "bg-[#D4AF37] border border-[#D4AF37] text-black opacity-100"
                : "bg-black/60 border border-white/30 text-transparent opacity-0 group-hover:opacity-100"
            }`}
            title={selected ? "Deselect" : "Select for download"}
          >
            <input
              type="checkbox"
              className="sr-only"
              checked={!!selected}
              onChange={onToggleSelect}
              data-testid={`photoroom-select-${job.id}`}
            />
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              className="w-3.5 h-3.5"
              aria-hidden="true"
            >
              <path d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4L8.5 12l6.8-6.7a1 1 0 011.4 0z" />
            </svg>
          </label>
        )}

        <button
          onClick={onDelete}
          title="Delete touch-up"
          className="absolute top-1.5 right-1.5 p-1 rounded-full bg-black/70 opacity-0 group-hover:opacity-100 transition text-red-300"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="px-3 py-2 text-xs">
        <div className="truncate text-white/80" title={job.source_filename}>{job.source_filename}</div>
        <div className="flex items-center justify-between mt-1 text-white/45">
          <span>{job.quality.toUpperCase().replace("_", " ")} · {job.output_format.toUpperCase()}</span>
          {isDone && (
            <div className="flex items-center gap-2">
              {job.jpeg_image_id && (
                <a
                  href={photoroomImageUrl(userId, job.jpeg_image_id, job.completed_at)}
                  download={`${job.source_filename}_${job.quality}.jpg`}
                  className="hover:text-[#D4AF37]"
                >JPG</a>
              )}
              {job.png_image_id && (
                <a
                  href={photoroomImageUrl(userId, job.png_image_id, job.completed_at)}
                  download={`${job.source_filename}_${job.quality}.png`}
                  className="hover:text-[#D4AF37]"
                >PNG</a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// Live progress bar shown inside an in-progress JobCard. Reads
// `job.progress` (0-1) and `job.progress_label` set by the backend worker
// at each pipeline step. Falls back to an indeterminate spinner if the
// job pre-dates the progress refactor (no `progress` field).
//
// ETA is computed two ways, fallback-chain:
//   1. If we have BOTH `started_at` AND progress > 10 %, extrapolate
//      remaining = elapsed × (1 - p) / p. This is grounded in the
//      user's actual machine + queue state.
//   2. Otherwise (queued, just-started, very early progress) we show a
//      tier baseline so the user still sees a number instead of "—".
//      Baselines are conservative; AuraSR queue can spike to 60-90 s.
const TIER_BASELINE_SECS_PER_FORMAT = {
  hd:           20,
  ultra_hd:     30,
  "4k":         45,
  print_master: 75,
};

function _formatEta(secs) {
  if (!secs || secs <= 0) return null;
  if (secs < 60) return `~${Math.max(1, Math.round(secs))}s left`;
  const mins = Math.round(secs / 60);
  return `~${mins} min left`;
}

function computeEta(job) {
  const baseline = TIER_BASELINE_SECS_PER_FORMAT[job.quality] || 30;
  const formats = job.output_format === "both" ? 2 : 1;
  // Within a single job, jpeg + png run in parallel (asyncio.gather),
  // so wall-clock for BOTH ≈ 1.15 × single-format, not 2 ×.
  const baselineTotal = baseline * (formats === 2 ? 1.15 : 1.0);

  if (job.status === "queued" || !job.started_at) {
    return `~${Math.round(baselineTotal)}s · queued`;
  }
  const startedMs = Date.parse(job.started_at);
  if (Number.isNaN(startedMs)) return _formatEta(baselineTotal);
  const elapsed = Math.max(0, (Date.now() - startedMs) / 1000);
  const p = typeof job.progress === "number" ? job.progress : 0;
  if (p > 0.1) {
    const total = elapsed / p;
    const remaining = total - elapsed;
    return _formatEta(remaining) || "~almost done";
  }
  // Very early — use baseline minus what's already elapsed.
  return _formatEta(Math.max(5, baselineTotal - elapsed));
}

function JobProgress({ job }) {
  const hasProgress = typeof job.progress === "number";
  const pct = hasProgress ? Math.round(Math.max(0, Math.min(1, job.progress)) * 100) : null;
  const label = job.progress_label || (job.status === "queued" ? "Waiting in queue…" : "Processing…");
  const eta = computeEta(job);
  if (!hasProgress) {
    return (
      <div className="flex flex-col items-center gap-2">
        <Loader2 className="w-5 h-5 animate-spin" />
        <div className="text-[10px] text-white/40">{label}</div>
        {eta && <div className="text-[10px] text-[#D4AF37]/70 font-mono" data-testid={`photoroom-eta-${job.id}`}>{eta}</div>}
      </div>
    );
  }
  return (
    <div className="w-full px-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/70 text-center mb-2">
        {label}
      </div>
      <div className="relative h-1.5 bg-white/8 rounded-full overflow-hidden" data-testid={`photoroom-progress-${job.id}`}>
        <div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#D4AF37] to-[#F3CE5A] transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%` }}
        />
      </div>
      <div className="flex items-center justify-between text-[10px] mt-1.5 font-mono">
        <span className="text-white/45">{pct}%</span>
        {eta && <span className="text-[#D4AF37]/75" data-testid={`photoroom-eta-${job.id}`}>{eta}</span>}
      </div>
    </div>
  );
}
